import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuth } from '../../contexts/AuthContext';
import { registerSchema } from '../../utils/validationSchemas';
import LoadingSpinner from '../LoadingSpinner';
import './AuthForms.css';

const RegisterForm = ({ onSwitchToLogin }) => {
  const { register: registerUser, isLoading, error, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(registerSchema),
    mode: 'onBlur'
  });

  const onSubmit = async (data) => {
    clearError();
    // Remove confirmPassword from data before sending to API
    const { confirmPassword: _confirmPassword, ...userData } = data;
    const result = await registerUser(userData);
    if (result.success) {
      // Navigation will be handled by the app after auth state updates
      console.log('Registration successful');
    }
  };

  return (
    <div className="auth-form">
      <div className="auth-header">
        <h2>Create an account</h2>
        <p>Join ReadTheWord to start reading with your family</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="form">
        <div className="form-group">
          <label htmlFor="name">Full name *</label>
          <input
            id="name"
            type="text"
            {...register('name')}
            className={errors.name ? 'input-error' : ''}
            placeholder="Enter your full name"
            autoComplete="name"
          />
          {errors.name && (
            <span className="field-error">{errors.name.message}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            id="email"
            type="email"
            {...register('email')}
            className={errors.email ? 'input-error' : ''}
            placeholder="Enter your email"
            autoComplete="email"
          />
          {errors.email && (
            <span className="field-error">{errors.email.message}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="password">Password *</label>
          <div className="password-input-container">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              {...register('password')}
              className={errors.password ? 'input-error' : ''}
              placeholder="Create a password (min. 6 characters)"
              autoComplete="new-password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
          {errors.password && (
            <span className="field-error">{errors.password.message}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm password *</label>
          <div className="password-input-container">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              {...register('confirmPassword')}
              className={errors.confirmPassword ? 'input-error' : ''}
              placeholder="Confirm your password"
              autoComplete="new-password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
          {errors.confirmPassword && (
            <span className="field-error">{errors.confirmPassword.message}</span>
          )}
        </div>

        <button
          type="submit"
          className="submit-button"
          disabled={isLoading || isSubmitting}
        >
          {isLoading || isSubmitting ? (
            <LoadingSpinner size="small" />
          ) : (
            'Create an account'
          )}
        </button>

        <div className="form-footer">
          <p>
            Already have an account?{' '}
            <button
              type="button"
              className="link-button primary"
              onClick={onSwitchToLogin}
            >
              Sign in
            </button>
          </p>
        </div>
      </form>
    </div>
  );
};

export default RegisterForm;
